import { DocumentAccessJSON, DocumentAccessParams } from "permissions/resources/documents/_schemas/document-access-schema";
import { DocumentContentsAccessJSON, DocumentContentsAccessParams } from "permissions/resources/documents/_schemas/document-contents-access-schema";
import { GeneralDocumentAccessJSON, GeneralDocumentAccessParams } from "permissions/resources/documents/_schemas/general-document-access-schema";
import { DocumentAccess, DocumentContentsAccess, GeneralDocumentAccess } from "permissions/resources/documents/documents-permissions-resources";
import { KnowledgeBaseAccessJSON, KnowledgeBaseAccessParams } from "permissions/resources/knowledge-base/knowledge-base-access-schema";
import { KnowledgeBaseAccess } from "permissions/resources/knowledge-base/knowledge-base-permissions-resources";
import { EntityAgreementAccessJSON, EntityAgreementAccessParams } from "permissions/resources/legal-entities/_schemas/entity-agreement-access-schema";
import { LegalEntitiesPlatformAccessJSON, LegalEntitiesPlatformAccessParams } from "permissions/resources/legal-entities/_schemas/legal-entities-platform-access-schema";
import { LegalEntityAccessJSON, LegalEntityAccessParams } from "permissions/resources/legal-entities/_schemas/legal-entity-access-schema";
import { LegalEntitiesPlatformAccess, EntityAgreementAccess, LegalEntityAccess } from "permissions/resources/legal-entities/legal-entities-permissions-resources";
import { MarketplaceAccess, MarketplaceDirectoryAccess, MarketplaceSearchAccess } from "permissions/resources/marketplace/marketplace-permissions-resources";
import { MarketplaceAccessJSON, MarketplaceAccessParams } from "permissions/resources/marketplace/_schema/marketplace-access-schema";
import { CommunicationsAccessJSON, CommunicationsAccessParams } from "permissions/resources/messaging/_schemas/communications-access-schema";
import { ForumAccessJSON, ForumAccessParams } from "permissions/resources/messaging/_schemas/forum-access-schema";
import { CommunicationsAccess, ForumAccess } from "permissions/resources/messaging/messaging-permissions-resources";
import { NotificationsAccess } from "permissions/resources/notifications/notifications-permissions-resources";
import { NotificationsAccessJSON, NotificationsAccessParams } from "permissions/resources/notifications/notifications-permissions-resources-schema";
import type { Resource } from "permissions/resources/permission-resource";
import { PermissionResourceJSON, PermissionResourceParams } from "permissions/resources/_schemas/permission-resource-schema";
import { LoginAccessJSON, LoginAccessParams } from "permissions/resources/users/_schemas/login-access-schema";
import { UserNetworkingAccessJSON, UserNetworkingAccessParams } from "permissions/resources/users/_schemas/user-networking-access-schema";
import { LoginAccess, UserNetworkingAccess } from "permissions/resources/users/users-permissions-resources";
import { ContractsAccessJSON, ContractsAccessParams } from "permissions/resources/work/_schemas/contracts-access-schema";
import { CurrencyFieldsAccessJSON, CurrencyFieldsAccessParams } from "permissions/resources/work/_schemas/currency-fields-access-schema";
import { DiscountsAccessJSON, DiscountsAccessParams } from "permissions/resources/work/_schemas/discounts-access-schema";
import { FeeScheduleTemplateAccessJSON, FeeScheduleTemplateAccessParams } from "permissions/resources/work/_schemas/fee-schedule-template-access-schema";
import { FeeSchedulesAccessJSON, FeeSchedulesAccessParams } from "permissions/resources/work/_schemas/fee-schedules-access-schema";
import { InvoicesAccessJSON, InvoicesAccessParams } from "permissions/resources/work/_schemas/invoices-access-schema";
import { ProposalAccessJSON, ProposalAccessParams } from "permissions/resources/work/_schemas/proposal-access-schema";
import { ProposalRequestAccessJSON, ProposalRequestAccessParams } from "permissions/resources/work/_schemas/proposal-request-access-schema";
import { ProposalsAccessJSON, ProposalsAccessParams } from "permissions/resources/work/_schemas/proposals-access-schema";
import { TeamTemplateAccessJSON, TeamTemplateAccessParams } from "permissions/resources/work/_schemas/team-template-access-schema";
import { TeamsAccessJSON, TeamsAccessParams } from "permissions/resources/work/_schemas/teams-access-schema";
import { WorkAgreementAccessJSON, WorkAgreementAccessParams } from "permissions/resources/work/_schemas/work-agreement-access-schema";
import { WorkAgreementsAccessJSON, WorkAgreementsAccessParams } from "permissions/resources/work/_schemas/work-agreements-access-schema";
import { WorkAgreementsAccess, ProposalsAccess, TeamsAccess, ContractsAccess, FeeSchedulesAccess, CurrencyFieldsAccess, DiscountsAccess, InvoicesAccess, ProposalAccess, WorkAgreementAccess, ProposalRequestAccess, FeeScheduleTemplateAccess, TeamTemplateAccess } from "permissions/resources/work/work-permissions-resources";
import { MarketplaceSearchAccessJSON, MarketplaceSearchAccessParams } from "permissions/resources/marketplace/_schema/marketplace-search-access-schema";
import { MarketplaceDirectoryAccessJSON, MarketplaceDirectoryAccessParams } from "permissions/resources/marketplace/_schema/marketplace-directory-access-schema";

import type Guid from "_shared/guids/guid";
import { CalendarEventAccessJSON, CalendarEventAccessParams } from "permissions/resources/calendar/_schemas/calendar-event-access-schema";
import { CalendarEventAccess } from "permissions/resources/calendar/calendar-permissions-resources";
// A constructor for any Resource subclass
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ResourceConstructor<T extends Resource = Resource> = new (params?: any) => T;

export interface BaseResourceParams {
  resourceId?: Guid;
  isDisabled?: boolean;
}

export enum AccessLevel {
  "Read-Only" = 0,
  "Read/Write" = 1,
}
export enum Scope {
  "Need-to-Know" = 0,
  "Entity-Wide" = 1,
  "Global" = 2
}
export enum ResourceCategory {
  "Communication",
  "Financial",
  "Work",
  "Documents",
  "Marketplace",
  "Account",
  "KnowledgeBase",
}
export enum ResourceType {
  CalendarEventAccess = "CalendarEventAccess",
  GeneralDocumentAccess = "GeneralDocumentAccess",
  DocumentContentsAccess = "DocumentContentsAccess",
  DocumentAccess = "DocumentAccess",
  KnowledgeBaseAccess = "KnowledgeBaseAccess",
  LegalEntitiesPlatformAccess = "LegalEntitiesPlatformAccess",
  EntityAgreementAccess = "EntityAgreementAccess",
  LegalEntityAccess = "LegalEntityAccess",
  MarketplaceAccess = "MarketplaceAccess",
  MarketplaceSearchAccess = "MarketplaceSearchAccess",
  MarketplaceDirectoryAccess = "MarketplaceDirectoryAccess",
  CommunicationsAccess = "CommunicationsAccess",
  ForumAccess = "ForumAccess",
  NotificationsAccess = "NotificationsAccess",
  LoginAccess = "LoginAccess",
  UserNetworkingAccess = "UserNetworkingAccess",
  WorkAgreementsAccess = "WorkAgreementsAccess",
  ProposalsAccess = "ProposalsAccess",
  TeamsAccess = "TeamsAccess",
  ContractsAccess = "ContractsAccess",
  FeeSchedulesAccess = "FeeSchedulesAccess",
  CurrencyFieldsAccess = "CurrencyFieldsAccess",
  DiscountsAccess = "DiscountsAccess",
  InvoicesAccess = "InvoicesAccess",
  ProposalAccess = "ProposalAccess",
  WorkAgreementAccess = "WorkAgreementAccess",
  ProposalRequestAccess = "ProposalRequestAccess",
  FeeScheduleTemplateAccess = "FeeScheduleTemplateAccess",
  TeamTemplateAccess = "TeamTemplateAccess",
}
export enum ChildResourceType {
  DocumentAccess = "DocumentAccess",
  EntityAgreementAccess = "EntityAgreementAccess",
  ForumAccess = "ForumAccess",
  ProposalAccess = "ProposalAccess",
  WorkAgreementAccess = "WorkAgreementAccess",
  ProposalRequestAccess = "ProposalRequestAccess",
  FeeScheduleTemplateAccess = "FeeScheduleTemplateAccess",
  TeamTemplateAccess = "TeamTemplateAccess",
}

export type AnyResourceJSON =
  | PermissionResourceJSON
  | CalendarEventAccessJSON
  | GeneralDocumentAccessJSON
  | DocumentContentsAccessJSON
  | DocumentAccessJSON
  | LegalEntitiesPlatformAccessJSON
  | EntityAgreementAccessJSON
  | LegalEntityAccessJSON
  | CommunicationsAccessJSON
  | ForumAccessJSON
  | MarketplaceAccessJSON
  | MarketplaceSearchAccessJSON
  | MarketplaceDirectoryAccessJSON
  | NotificationsAccessJSON
  | LoginAccessJSON
  | UserNetworkingAccessJSON
  | WorkAgreementsAccessJSON
  | ProposalsAccessJSON
  | TeamsAccessJSON
  | ContractsAccessJSON
  | FeeSchedulesAccessJSON
  | CurrencyFieldsAccessJSON
  | DiscountsAccessJSON
  | InvoicesAccessJSON
  | ProposalAccessJSON
  | WorkAgreementAccessJSON
  | KnowledgeBaseAccessJSON
  | ProposalRequestAccessJSON
  | FeeScheduleTemplateAccessJSON
  | TeamTemplateAccessJSON;

  export type AnyResourceParams = PermissionResourceParams 
  | CalendarEventAccessParams
  | GeneralDocumentAccessParams
  | DocumentContentsAccessParams
  | DocumentAccessParams
  | LegalEntitiesPlatformAccessParams
  | EntityAgreementAccessParams
  | LegalEntityAccessParams
  | CommunicationsAccessParams
  | ForumAccessParams
  | MarketplaceAccessParams
  | MarketplaceSearchAccessParams
  | MarketplaceDirectoryAccessParams
  | NotificationsAccessParams
  | LoginAccessParams
  | UserNetworkingAccessParams
  | WorkAgreementsAccessParams
  | ProposalsAccessParams
  | TeamsAccessParams
  | ContractsAccessParams
  | FeeSchedulesAccessParams
  | CurrencyFieldsAccessParams
  | DiscountsAccessParams
  | InvoicesAccessParams
  | ProposalAccessParams
  | WorkAgreementAccessParams
  | KnowledgeBaseAccessParams
  | ProposalRequestAccessParams
  | FeeScheduleTemplateAccessParams
  | TeamTemplateAccessParams;

export type AnyResource = Resource 
  | CalendarEventAccess
  | GeneralDocumentAccess
  | DocumentContentsAccess
  | DocumentAccess
  | LegalEntitiesPlatformAccess
  | EntityAgreementAccess
  | LegalEntityAccess
  | CommunicationsAccess
  | ForumAccess
  | MarketplaceAccess
  | MarketplaceSearchAccess
  | MarketplaceDirectoryAccess
  | NotificationsAccess
  | LoginAccess
  | UserNetworkingAccess
  | WorkAgreementsAccess
  | ProposalsAccess
  | TeamsAccess
  | ContractsAccess
  | FeeSchedulesAccess
  | CurrencyFieldsAccess
  | DiscountsAccess
  | InvoicesAccess
  | ProposalAccess
  | WorkAgreementAccess
  | KnowledgeBaseAccess
  | ProposalRequestAccess
  | FeeScheduleTemplateAccess
  | TeamTemplateAccess;

export type ResourceFromJSON<J extends AnyResourceJSON> = J extends { resourceType: ResourceType.GeneralDocumentAccess }
  ? GeneralDocumentAccess
  : J extends { resourceType: ResourceType.CalendarEventAccess }
  ? CalendarEventAccess
  : J extends { resourceType: ResourceType.DocumentContentsAccess }
  ? DocumentContentsAccess
  : J extends { resourceType: ResourceType.DocumentAccess }
  ? DocumentAccess
  : J extends { resourceType: ResourceType.LegalEntitiesPlatformAccess }
  ? LegalEntitiesPlatformAccess
  : J extends { resourceType: ResourceType.EntityAgreementAccess }
  ? EntityAgreementAccess
  : J extends { resourceType: ResourceType.LegalEntityAccess }
  ? LegalEntityAccess
  : J extends { resourceType: ResourceType.CommunicationsAccess }
  ? CommunicationsAccess
  : J extends { resourceType: ResourceType.ForumAccess }
  ? ForumAccess
  : J extends { resourceType: ResourceType.MarketplaceAccess }
  ? MarketplaceAccess
  : J extends { resourceType: ResourceType.MarketplaceSearchAccess }
  ? MarketplaceSearchAccess
  : J extends { resourceType: ResourceType.MarketplaceDirectoryAccess }
  ? MarketplaceDirectoryAccess
  : J extends { resourceType: ResourceType.NotificationsAccess }
  ? NotificationsAccess
  : J extends { resourceType: ResourceType.LoginAccess }
  ? LoginAccess
  : J extends { resourceType: ResourceType.UserNetworkingAccess }
  ? UserNetworkingAccess
  : J extends { resourceType: ResourceType.WorkAgreementsAccess }
  ? WorkAgreementsAccess
  : J extends { resourceType: ResourceType.ProposalsAccess }
  ? ProposalsAccess
  : J extends { resourceType: ResourceType.TeamsAccess }
  ? TeamsAccess
  : J extends { resourceType: ResourceType.ContractsAccess }
  ? ContractsAccess
  : J extends { resourceType: ResourceType.FeeSchedulesAccess }
  ? FeeSchedulesAccess
  : J extends { resourceType: ResourceType.CurrencyFieldsAccess }
  ? CurrencyFieldsAccess
  : J extends { resourceType: ResourceType.DiscountsAccess }
  ? DiscountsAccess
  : J extends { resourceType: ResourceType.InvoicesAccess }
  ? InvoicesAccess
  : J extends { resourceType: ResourceType.ProposalAccess }
  ? ProposalAccess
  : J extends { resourceType: ResourceType.WorkAgreementAccess }
  ? WorkAgreementAccess
  : J extends { resourceType: ResourceType.KnowledgeBaseAccess }
  ? KnowledgeBaseAccess
  : J extends { resourceType: ResourceType.ProposalRequestAccess }
  ? ProposalRequestAccess
  : J extends { resourceType: ResourceType.FeeScheduleTemplateAccess }
  ? FeeScheduleTemplateAccess
  : J extends { resourceType: ResourceType.TeamTemplateAccess }
  ? TeamTemplateAccess
  : AnyResource; // fallback
